import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SidebarStateService } from '@zikata-frontend/ui';
import { AuthService } from '../../core/service/auth.service';
import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

type ParamFunction<T> = (params: Record<string, string>) => T;

interface RouteInfo {
  path: string;
  title: string | ParamFunction<string>;
  breadcrumb: string[] | ParamFunction<string[]>;
}

@Component({
  selector: 'app-default-layout',
  template: `
    <zikatagov-toasts></zikatagov-toasts>
    <div class="d-flex vh-100 position-relative">
      <div
        class="sidebar-overlay"
        [class.active]="!isCollapsed && isMobile"
        (click)="closeSidebarOnMobile()"
      ></div>
      <zikatagov-sidebar
        [class.mobile-sidebar]="isMobile"
        (menuItemClick)="onMenuItemClick($event)"
        (logout)="handleLogout()"
      ></zikatagov-sidebar>
      <div
        class="flex-grow-1 d-flex flex-column bg-zikata"
        [ngClass]="{
          'content-expanded': !isCollapsed,
          'content-mobile': isMobile
        }"
      >
        <div class="d-flex align-items-center shadow-sm">
          <button
            *ngIf="isMobile"
            class="btn btn-link d-md-none ms-2"
            (click)="toggleSidebar()"
            aria-label="Toggle menu"
          >
            <ng-icon
              name="saxMenuOutline"
              size="15"
              class="text-white"
            ></ng-icon>
          </button>
          <zikatagov-header class="w-100"></zikatagov-header>
        </div>
        <main class="flex-grow-1 overflow-auto px-3 pt-3 bg-light">
          <div class="container-fluid">
            <zikatagov-page-header></zikatagov-page-header>
            <router-outlet></router-outlet>
          </div>
        </main>
      </div>
    </div>
  `,
  styleUrls: ['./default-layout.component.scss'],
})
export class DefaultLayoutComponent implements OnInit, OnDestroy {
  isCollapsed = false;
  isMobile = false;
  private sidebarSubscription!: Subscription;
  private resizeSubscription!: Subscription;

  private routeMap: Record<number, RouteInfo> = {
    0: {
      path: '/dashboard/home',
      title: 'Home',
      breadcrumb: ['Dashboard', 'Home'],
    },
    3: {
      path: '/dashboard/applications',
      title: 'My Applications',
      breadcrumb: ['Dashboard', 'Applications'],
    },
    9: {
      path: '/dashboard/alerts',
      title: 'Alerts',
      breadcrumb: ['Dashboard', 'Alerts'],
    },
    4: {
      path: '/dashboard/developer',
      title: 'Developer',
      breadcrumb: ['Dashboard', 'Developer'],
    },
    5: {
      path: '/dashboard/transaction',
      title: 'Transactions',
      breadcrumb: ['Dashboard', 'Transactions'],
    },
    6: {
      path: '/dashboard/users',
      title: 'Display Users',
      breadcrumb: ['Dashboard', 'Users'],
    },
    7: {
      path: '/dashboard/contacts',
      title: 'Contacts',
      breadcrumb: ['Dashboard', 'Contacts'],
    },
    8: {
      path: '/dashboard/campaign',
      title: 'SMS Builder',
      breadcrumb: ['Dashboard', 'Campaign'],
    },
    11: {
      path: '/contact-management/campaigns',
      title: 'Bulk SMS',
      breadcrumb: ['Dashboard', 'SMS', 'Bulk SMS'],
    },
    10: {
      path: '/contact-management/groups',
      title: 'Contact Groups',
      breadcrumb: ['Dashboard', 'Contact', 'Contact Groups'],
    },

    12: {
      path: '/contact-management/surveys',
      title: 'Surveys',
      breadcrumb: ['Dashboard', 'Surveys'],
    },

    100: {
      path: '/contact-management/groups/:id',
      title: (params: Record<string, string>) => {
        console.log('params::', params);
        return `Contact Groups`;
      },
      breadcrumb: (params: Record<string, string>) => [
        'Dashboard',
        'Contact',
        'Contact Groups',
        'Updating',
      ],
    },
    103: {
      path: '/contact-management/surveys/form/:id',
      title: (params: Record<string, string>) => {
        console.log('params::', params);
        return `Survey`;
      },
      breadcrumb: (params: Record<string, string>) => [
        'Dashboard',
        'Survey',
        'Updating',
      ],
    },
    105: {
      path: '/contact-management/surveys/questions/:id',
      title: (params: Record<string, string>) => {
        console.log('params::', params);
        return `Survey Question`;
      },
      breadcrumb: (params: Record<string, string>) => [
        'Dashboard',
        'Survey',
        'Questions',
        'Updating',
      ],
    },
    106: {
      path: '/contact-management/surveys/outro/:id',
      title: (params: Record<string, string>) => {
        console.log('params::', params);
        return `Survey Outro`;
      },
      breadcrumb: (params: Record<string, string>) => [
        'Dashboard',
        'Survey',
        'Outro',
      ],
    },
    107: {
      path: '/contact-management/surveys/send/:id',
      title: (params: Record<string, string>) => {
        console.log('params::', params);
        return `Publish Survey`;
      },
      breadcrumb: (params: Record<string, string>) => [
        'Dashboard',
        'Survey',
        'Send Survey',
        'Updating',
      ],
    },
    108: {
      path: '/contact-management/surveys/responses/:id',
      title: (params: Record<string, string>) => {
        console.log('params::', params);
        return `Survey Responses`;
      },
      breadcrumb: (params: Record<string, string>) => [
        'Dashboard',
        'Survey',
        'Responses',
      ],
    },

    101: {
      path: '/contact-management/campaigns/new',
      title: 'Bulk SMS',
      breadcrumb: ['Dashboard', 'SMS', 'Bulk SMS', 'New'],
    },
    104: {
      path: '/contact-management/surveys/form/new',
      title: 'Survey',
      breadcrumb: ['Dashboard', 'Survey', 'New', 'New'],
    },

    109: {
      path: '/contact-management/campaigns/form/:id',
      title: (params: Record<string, string>) => `Edit Bulk SMS Campaign`,
      breadcrumb: ['Dashboard', 'SMS', 'Bulk SMS', 'Updating'],
    },
    110: {
      path: '/contact-management/groups/new',
      title: 'New Contact Group',
      breadcrumb: ['Dashboard', 'Contact', 'Contact Groups', 'New'],
    },
    111: {
      path: '/contact-management/groups/:id/edit',
      title: (params: Record<string, string>) => 'Edit Contact Group',
      breadcrumb: ['Dashboard', 'Contact', 'Contact Groups', 'Edit'],
    },
    112: {
      path: '/contact-management/groups/:id/import',
      title: (params: Record<string, string>) => 'Import Contacts',
      breadcrumb: ['Dashboard', 'Contact', 'Contact Groups', 'Import'],
    },
    113: {
      path: '/contact-management/groups/:id/contacts/new',
      title: (params: Record<string, string>) => 'New Contact',
      breadcrumb: ['Dashboard', 'Contact', 'Contact Groups', 'New Contact'],
    },
    114: {
      path: '/contact-management/groups/:id/contacts/:contactId/edit',
      title: (params: Record<string, string>) => 'Edit Contact',
      breadcrumb: ['Dashboard', 'Contact', 'Contact Groups', 'Edit Contact'],
    },
    115: {
      path: '/dashboard/whatsapp',
      title: 'WhatsApp Messages',
      breadcrumb: ['Dashboard', 'WhatsApp'],
    },
    116: {
      path: '/dashboard/summary',
      title: 'Summary',
      breadcrumb: ['Dashboard', 'Summary'],
    },
  };

  constructor(
    private router: Router,
    private sidebarStateService: SidebarStateService,
    private authService: AuthService
  ) {
    this.checkScreenSize();
  }

  ngOnInit(): void {
    this.sidebarSubscription = this.sidebarStateService.isCollapsed$.subscribe(
      (isCollapsed) => (this.isCollapsed = isCollapsed)
    );

    // Add window resize listener
    this.resizeSubscription = fromEvent(window, 'resize')
      .pipe(debounceTime(250))
      .subscribe(() => {
        this.checkScreenSize();
      });
  }

  ngOnDestroy(): void {
    if (this.sidebarSubscription) {
      this.sidebarSubscription.unsubscribe();
    }
    if (this.resizeSubscription) {
      this.resizeSubscription.unsubscribe();
    }
  }

  private checkScreenSize(): void {
    this.isMobile = window.innerWidth < 768; // Bootstrap's md breakpoint
    if (this.isMobile) {
      this.sidebarStateService.collapse(true);
    }
  }

  closeSidebarOnMobile(): void {
    if (this.isMobile) {
      this.sidebarStateService.collapse(true);
    }
  }

  onMenuItemClick(id: number): void {
    const routeInfo = this.routeMap[id];
    if (routeInfo) {
      this.router.navigate([routeInfo.path]);
    } else {
      console.warn(`No route mapped for ID ${id}`);
    }
  }

  handleLogout() {
    this.authService.logout();
  }

  toggleSidebar(): void {
    this.sidebarStateService.toggleSidebar();
  }
}
